import type { FC } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { RouterLink } from "src/components/router-link";
import { useCookieConsent } from "src/contexts/cookie-consent-context";
import { paths } from "src/paths";

export const CookieBanner: FC = () => {
  const { t } = useTranslation();
  const { isReady, bannerOpen, acceptAll, acceptNecessaryOnly } =
    useCookieConsent();

  if (!isReady || !bannerOpen) {
    return null;
  }

  return (
    <Paper
      component="aside"
      elevation={0}
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.snackbar,
        borderRadius: 0,
        borderTop: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (theme) => theme.shadows[16],
        pb: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 2.5 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 4 }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              id="cookie-banner-title"
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              {t("cookieBannerTitle", "Cookies")}
            </Typography>
            <Typography
              id="cookie-banner-description"
              variant="body2"
              color="text.secondary"
            >
              {t(
                "cookieBannerBody",
                "We use necessary cookies to run this website and keep you signed in. With your permission we also use analytics and advertising cookies to improve the site and measure campaigns. Read our"
              )}{" "}
              <Link
                component={RouterLink}
                href={paths.cookiePolicy}
                underline="always"
                color="text.primary"
                variant="body2"
              >
                {t("cookiePolicy", "Cookie Policy")}
              </Link>
              .
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={acceptNecessaryOnly}
              sx={{
                minWidth: { sm: 168 },
                py: 1.1,
                color: "text.primary",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "text.primary",
                  bgcolor: "action.hover",
                },
              }}
            >
              {t("cookieNecessaryOnly", "Necessary only")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={acceptAll}
              sx={{ minWidth: { sm: 168 }, py: 1.1 }}
            >
              {t("cookieAcceptAll", "Accept all")}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Paper>
  );
};
